import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/base/Seo";
import { site } from "@/mocks/site";
import { categories } from "@/mocks/categories";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "真实第一",
    desc: "每一条圈内速递均经过多方信源核实，避免未经证实的小道消息误导读者。",
  },
  {
    icon: "ri-sword-line",
    title: "中立客观",
    desc: "不偏袒任何一方，如实呈现事件脉络与多方观点，让读者自行判断。",
  },
  {
    icon: "ri-heart-3-line",
    title: "尊重为先",
    desc: "关注热点同时尊重当事人，不做恶意揣测与人身攻击，理性吃瓜。",
  },
  {
    icon: "ri-flashlight-line",
    title: "及时速递",
    desc: "热点响应力求快速，重大圈内事件第一时间整理上线，不让你错过。",
  },
];

const testimonials = [
  {
    name: "林小满",
    role: "资深追星族",
    avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20friendly%20young%20chinese%20woman%20smiling%2C%20soft%20light%2C%20clean%20background%2C%20social%20avatar%20photography&width=200&height=200&seq=av-test-01&orientation=squarish",
    text: "吃瓜视频网更新超快，明星行程和综艺高光基本当天就能看到，追星党的福音！",
  },
  {
    name: "陈默",
    role: "影视爱好者",
    avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20casual%20young%20chinese%20man%20smiling%20with%20glasses%2C%20soft%20light%2C%20clean%20background%2C%20social%20avatar%20photography&width=200&height=200&seq=av-test-02&orientation=squarish",
    text: "影视花絮栏目做得特别用心，幕后故事和预告解析都很有深度，看得出是认真在做内容。",
  },
  {
    name: "王佳佳",
    role: "红人视频观众",
    avatar: "https://readdy.ai/api/search-image?query=Portrait%20of%20cheerful%20young%20chinese%20woman%20with%20ponytail%20smiling%2C%20soft%20light%2C%20clean%20background%2C%20social%20avatar%20photography&width=200&height=200&seq=av-test-03&orientation=squarish",
    text: "红人专区推荐的质量很高，好几个喜欢的创作者都是在这里发现的，界面也清爽好刷。",
  },
];

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于吃瓜视频网",
  url: "https://bknos.cn/about",
  description: site.description,
  about: {
    "@type": "Organization",
    name: "吃瓜视频网",
    email: site.contact.email,
    slogan: site.slogan,
  },
};

export default function AboutPage() {
  return (
    <Layout>
      <Seo
        title="关于我们 - 吃瓜视频网品牌介绍与内容理念"
        description="了解吃瓜视频网的品牌介绍、网站定位、发展理念与内容原则，认识这个专注娱乐八卦与圈内速递的视频资讯平台。"
        keywords="吃瓜视频网,关于我们,品牌介绍,内容原则,娱乐八卦"
        canonical="https://bknos.cn/about"
        type="about"
        jsonLd={aboutJsonLd}
      />

      {/* Hero */}
      <section className="bg-primary-600 py-14">
        <div className="mx-auto max-w-7xl px-4 text-center md:px-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-200">About Us</p>
          <h1 className="mt-3 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            让追星吃瓜，变成一件轻松的事
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-background-50/90">
            吃瓜视频网是一个专注于娱乐八卦与圈内速递的视频资讯平台，致力于把娱乐圈最鲜活、最真实的新鲜事，用清爽的方式送到你面前。
          </p>
        </div>
      </section>

      {/* Brand story */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">品牌介绍</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              我们深知，追星和吃瓜本该是一种享受，而不是在铺天盖地的信息里费力淘金。因此，吃瓜视频网把明星动态、综艺片段、影视花絮和红人视频聚合在一起，用清晰的栏目和实时的排序，让你用一个入口就能掌握娱乐圈的全貌。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              从「想知道的」到「值得看的」，我们的编辑与内容推荐团队持续筛选、核实并整理圈内热点，让每一则速递都有价值、有依据、有温度。
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              {[
                { v: "4600+", l: "圈内速递" },
                { v: "12h", l: "热点响应" },
                { v: "4", l: "核心栏目" },
              ].map((s) => (
                <div key={s.l} className="rounded-xl bg-background-100 p-4 text-center ring-1 ring-inset ring-background-200">
                  <p className="font-heading text-2xl font-bold text-primary-600">{s.v}</p>
                  <p className="mt-1 text-xs text-foreground-500">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl">
            <img
              src="https://readdy.ai/api/search-image?query=Cheerful%20young%20creative%20team%20discussing%20near%20a%20wall%20covered%20with%20colorful%20entertainment%20moodboard%2C%20warm%20light%2C%20modern%20studio%2C%20editorial%20photography&width=900&height=640&seq=about-story-01&orientation=portrait"
              alt="吃瓜视频网团队讨论内容策划"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Principles */}
      <section id="principles" className="bg-background-100 py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">Our Principles</p>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
            <p className="mt-2 text-sm text-foreground-600">我们坚持的内容底线，也是每一位读者的信任来源。</p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-2xl bg-background-50 p-6 ring-1 ring-inset ring-background-200">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-500 text-background-50">
                  <i className={`${p.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="feedback" className="mx-auto max-w-7xl px-4 py-14 md:px-6">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">Voices</p>
          <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">用户口碑</h2>
          <p className="mt-2 text-sm text-foreground-600">来自真实用户的声音，是我们持续前行的最大动力。</p>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="flex flex-col rounded-2xl bg-background-50 p-6 ring-1 ring-inset ring-background-200">
              <i className="ri-double-quotes-l text-3xl text-primary-200" />
              <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-600">{t.text}</blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                <img src={t.avatar} alt={`${t.name}的头像`} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </div>
                <span className="ml-auto text-accent-400"><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /></span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-7xl px-4 pb-16 md:px-6">
        <div className="flex flex-col items-center justify-between gap-6 rounded-3xl bg-background-100 p-8 ring-1 ring-inset ring-background-200 md:flex-row md:p-10">
          <div className="text-center md:text-left">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">想了解更多或加入我们？</h2>
            <p className="mt-2 text-sm text-foreground-600">
              欢迎通过联系我们页面提交反馈、投稿或合作意向，我们期待听到你的声音。
            </p>
            <p className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-foreground-500 md:justify-start">
              <span className="flex items-center gap-1.5"><i className="ri-mail-line text-primary-500" /> {site.contact.email}</span>
              <span className="flex items-center gap-1.5"><i className="ri-wechat-line text-secondary-500" /> {site.contact.wechat}</span>
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600">
              <i className="ri-mail-send-line" /> 联系我们
            </Link>
            <Link to="/" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 ring-1 ring-inset ring-background-300 transition hover:text-primary-600">
              回到首页
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}